import { Button, Typography } from '@mui/material';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useBoolean } from 'usehooks-ts';

import { StatusIcon } from '@/components/DataDisplay/StatusIcon';
import { ActionAccordion } from '@/components/Form/ActionList/ActionAccordion';
import { DocumentActionItem } from '@/components/Form/ActionList/DocumentActionItem';
import { ReviewMyInfo } from '@/components/PageForm/MoreInfoForm/ReviewMyInfo/ReviewMyInfo';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import { DocumentStatus } from '@/types/document';

import { ReviewAndSubmitContent } from './ReviewAndSubmitContent';

import { ReviewPrompt } from './ReviewAndSubmit.styled';

type ReviewAndSubmitSectionProps = {
  disabled?: boolean;
};

export function ReviewAndSubmitSection({
  disabled,
}: Readonly<ReviewAndSubmitSectionProps>) {
  const { t } = useTranslate();
  const user = useAppSelector(selectLoggedInUser);

  const { value: isExpanded, setValue: setExpanded } = useBoolean(false);
  const {
    value: isReviewOpen,
    setTrue: openReview,
    setFalse: closeReview,
  } = useBoolean(false);
  const [resetAgreementKey, setResetAgreementKey] = useState(0);

  const isConfirmed = !!user?.infoAccuracyConfirmed;

  useEffect(() => {
    if (disabled) {
      setExpanded(false);
      setResetAgreementKey(key => key + 1);
    }
  }, [disabled, setExpanded]);

  useEffect(() => {
    if (!isConfirmed) {
      setResetAgreementKey(key => key + 1);
    }
  }, [isConfirmed, user?.id]);

  let description = t('moreInfo.form.warranty.description.active');
  if (disabled) {
    description = t('moreInfo.form.warranty.description.inactive');
  } else if (isConfirmed) {
    description = t('status.completed');
  }

  const handleAccordionExpand = (_event: SyntheticEvent, expanded: boolean) => {
    if (isConfirmed || disabled) {
      setExpanded(false);
      return;
    }
    if (!expanded) {
      setResetAgreementKey(key => key + 1);
    }
    setExpanded(expanded);
  };

  const expandIcon =
    isConfirmed && !disabled ? (
      <StatusIcon value={DocumentStatus.Approved} />
    ) : (
      <StatusIcon
        value=""
        iconName="signature"
        color={theme =>
          disabled ? theme.palette.neutral[700] : theme.palette.primaries[400]
        }
        backgroundColor={theme =>
          disabled ? theme.palette.neutral[200] : theme.palette.primaries[100]
        }
      />
    );

  if (isConfirmed) {
    return (
      <>
        <DocumentActionItem
          title={t('moreInfo.form.warranty.reviewAndSubmit.title')}
          status={DocumentStatus.Completed}
          hideAction
        />
        <ReviewMyInfo
          hideTrigger
          open={isReviewOpen}
          onClose={closeReview}
          disabled={disabled}
        />
      </>
    );
  }

  return (
    <>
      <ActionAccordion
        title={t('moreInfo.form.warranty.reviewAndSubmit.title')}
        description={description}
        expanded={isConfirmed || disabled ? false : isExpanded}
        onChange={handleAccordionExpand}
        disabled={disabled}
        expandIcon={expandIcon}
        disableExpandIconRotation
      >
        <ReviewAndSubmitContent
          disabled={disabled}
          resetAgreementKey={resetAgreementKey}
        />

        <ReviewPrompt>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('moreInfo.form.reviewAndSubmit.reviewPrompt')}
          </Typography>
          <Button
            fullWidth
            variant="outlined"
            onClick={openReview}
            disabled={disabled}
          >
            {t('moreInfo.reviewMyInformation.button')}
          </Button>
        </ReviewPrompt>
      </ActionAccordion>

      <ReviewMyInfo
        hideTrigger
        open={isReviewOpen}
        onClose={closeReview}
        disabled={disabled}
      />
    </>
  );
}
